import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSpinner } from '@angular/material/progress-spinner';
import {
  AiExtractionPayload,
  AiExtractionRequest,
  AiExtractionResult,
} from '@intellio/shared/models';
import {
  AiExtractionService,
  AnalyticsService,
} from '@intellio/shared/services';
import { ApplicationFacade } from '@intellio/shared/state-mgmt';
import { getFormRoot } from '@intellio/shared/utils';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import { concatMap, defer, finalize, Subject, take, takeUntil } from 'rxjs';

interface AiExtractionProps extends FormlyFieldProps {
  buttonText?: string;
  fileKey?: string;
  payload?: AiExtractionPayload;
  resultFields?: string[];
}

export interface AiExtractionConfig
  extends FormlyFieldConfig<AiExtractionProps> {
  type: 'ai-extraction';
}

@Component({
  selector: 'itc-ai-extraction',
  templateUrl: './ai-extraction.component.html',
  styleUrls: ['./ai-extraction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatSpinner],
})
export class AiExtractionComponent
  extends FieldType<FieldTypeConfig<AiExtractionProps>>
  implements OnInit, OnDestroy
{
  private readonly destroyed$ = new Subject<void>();
  private fileControl?: AbstractControl;
  private applicationId?: string;
  private extractionStartedAt = 0;
  private activeFormState?: Record<string, unknown>;

  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    private readonly aiExtractionService: AiExtractionService,
    private readonly applicationFacade: ApplicationFacade,
    private readonly analyticsService: AnalyticsService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.field.form?.root.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.changeDetectorRef.markForCheck());
    this.fileControl = this.field.form?.root.get(this.fileKey) ?? undefined;
    this.fileControl?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.errorMessage = null;
        this.changeDetectorRef.markForCheck();
      });

    this.applicationFacade.currentApplication$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((application) => {
        this.applicationId = application?.id;
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get buttonText(): string {
    return this.props.buttonText?.trim() || $localize`Extract Data`;
  }

  get fileKey(): string {
    return this.props.fileKey || '';
  }

  get fileName(): string | null {
    const value = this.fileControl
      ? this.fileControl.value
      : getFormRoot(this.field)?.model?.[this.fileKey];
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  get resultFields(): string[] {
    return this.props.resultFields ?? [];
  }

  get isDisabled(): boolean {
    return (
      this.isLoading ||
      this.options.formState?.isSaving === true ||
      this.options.formState?.isExtracting === true ||
      typeof this.options.formState?.saveBeforeExtraction !== 'function' ||
      !this.applicationId ||
      !this.fileName ||
      !this.props.payload?.ruleName?.trim() ||
      this.props.disabled === true ||
      this.field.form?.root.disabled === true
    );
  }

  extractData(): void {
    const applicationId = this.applicationId;
    const fileName = this.fileName;
    const ruleName = this.props.payload?.ruleName;
    const rootForm = this.field.form?.root;
    if (!applicationId || !fileName || !ruleName || this.isDisabled) {
      return;
    }

    const request: AiExtractionRequest = {
      ruleName,
      applicationId,
      fileName,
    };

    this.isLoading = true;
    this.activeFormState = this.options.formState ??= {};
    this.errorMessage = null;
    this.extractionStartedAt = Date.now();
    this.analyticsService.logEvent('AppFormsPerf.AiExtractionStarted');
    this.changeDetectorRef.markForCheck();

    let extractionStarted = false;
    defer(() => this.options.formState.saveBeforeExtraction())
      .pipe(
        take(1),
        concatMap(() => {
          extractionStarted = true;
          this.activeFormState.isExtracting = true;
          return this.aiExtractionService.extract(request).pipe(take(1));
        }),
        takeUntil(this.destroyed$),
        finalize(() => this.finishLoading())
      )
      .subscribe({
        next: (result) => {
          if (
            this.fileName !== fileName ||
            this.applicationId !== applicationId ||
            this.field.form?.root !== rootForm
          ) {
            return;
          }

          this.applyResult(result);
          this.analyticsService.logEvent('AppFormsPerf.AiExtractionCompleted', {
            durationMs: Date.now() - this.extractionStartedAt,
          });
        },
        error: () => {
          this.errorMessage = extractionStarted
            ? $localize`Unable to extract data. Try again.`
            : $localize`Unable to save the form. Data extraction was not started. Try again.`;
          this.analyticsService.logEvent('AppFormsPerf.AiExtractionFailed', {
            durationMs: Date.now() - this.extractionStartedAt,
          });
        },
      });
  }

  private applyResult(result: AiExtractionResult): void {
    const rootForm = this.field.form?.root;
    this.resultFields.forEach((key) => {
      const control = rootForm?.get(key);
      control?.setValue(result?.[key] ?? null);
      control?.markAsDirty();
    });
    rootForm?.markAsDirty();
    this.options.detectChanges?.(this.field);
  }

  private finishLoading(): void {
    if (this.activeFormState) {
      this.activeFormState.isExtracting = false;
      this.activeFormState = undefined;
    }
    this.isLoading = false;
    this.changeDetectorRef.markForCheck();
  }
}
